import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import type {
  JonumMissingUserInfo,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import {
  FiatWalletKycState,
  JonumMissingUserInfo as JonumMissingUserInfoEnum,
  JonumVerificationStatus,
} from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';

import { AccountVerificationStep } from './AccountVerification/type.ts';
import type { AccountVerificationData } from './AccountVerification/type.ts';
import type {
  CollectJonumPersonalInfoMutation,
  CollectJonumPersonalInfoMutationVariables,
  CreateJonumVerificationMutation,
  CreateJonumVerificationMutationVariables,
  JonumVerificationFields,
  SetJonumPlaytimeLimitMutation,
  SetJonumPlaytimeLimitMutationVariables,
  SetJonumSpendingLimitMutation,
  SetJonumSpendingLimitMutationVariables,
} from './__generated__/useJonumVerificationGuard.graphql.ts';

const JONUM_VERIFICATION_FIELDS = gql`
  fragment JonumVerificationFields on Verification {
    id
    status
    missingInfo
    firstName
    lastName
    dateOfBirth
  }
` as TypedDocumentNode<JonumVerificationFields>;

// Backend signals "user must complete JONUM verification" with this code on
// any mutation that requires it (loadSuggestedLineup, createOrUpdateSo5Lineup,
// …). Consumers open DialogKey.jonumVerification on match.
export const JONUM_VERIFICATION_REQUIRED_CODE = 25003;

const CREATE_JONUM_VERIFICATION = gql`
  mutation CreateJonumVerificationMutation(
    $input: createJonumVerificationInput!
  ) {
    createJonumVerification(input: $input) {
      currentUser {
        slug
        jonumVerification {
          id
          ...JonumVerificationFields
        }
      }
      errors {
        message
        code
      }
    }
  }
  ${JONUM_VERIFICATION_FIELDS}
` as TypedDocumentNode<
  CreateJonumVerificationMutation,
  CreateJonumVerificationMutationVariables
>;

const COLLECT_JONUM_PERSONAL_INFO = gql`
  mutation CollectJonumPersonalInfoMutation(
    $input: collectJonumPersonalInfoInput!
  ) {
    collectJonumPersonalInfo(input: $input) {
      verification {
        id
        ...JonumVerificationFields
      }
      errors {
        message
        code
      }
    }
  }
  ${JONUM_VERIFICATION_FIELDS}
` as TypedDocumentNode<
  CollectJonumPersonalInfoMutation,
  CollectJonumPersonalInfoMutationVariables
>;

const SET_JONUM_PLAYTIME_LIMIT = gql`
  mutation SetJonumPlaytimeLimitMutation($input: setJonumPlaytimeLimitInput!) {
    setJonumPlaytimeLimit(input: $input) {
      verification {
        id
        ...JonumVerificationFields
      }
      errors {
        message
        code
      }
    }
  }
  ${JONUM_VERIFICATION_FIELDS}
` as TypedDocumentNode<
  SetJonumPlaytimeLimitMutation,
  SetJonumPlaytimeLimitMutationVariables
>;

const SET_JONUM_SPENDING_LIMIT = gql`
  mutation SetJonumSpendingLimitMutation($input: setJonumSpendingLimitInput!) {
    setJonumSpendingLimit(input: $input) {
      verification {
        id
        ...JonumVerificationFields
      }
      errors {
        message
        code
      }
    }
  }
  ${JONUM_VERIFICATION_FIELDS}
` as TypedDocumentNode<
  SetJonumSpendingLimitMutation,
  SetJonumSpendingLimitMutationVariables
>;

const ACTIVE_STATUSES = new Set<string>([
  JonumVerificationStatus.UNVERIFIED,
  JonumVerificationStatus.INFO_COLLECTING,
]);

// When verification doesn't exist yet, assume all info will be needed
const ALL_MISSING_INFO: JonumMissingUserInfo[] = [
  JonumMissingUserInfoEnum.FIRST_NAME,
  JonumMissingUserInfoEnum.LAST_NAME,
  JonumMissingUserInfoEnum.DATE_OF_BIRTH,
  JonumMissingUserInfoEnum.PLAYTIME_LIMIT,
  JonumMissingUserInfoEnum.SPENDING_LIMIT,
  JonumMissingUserInfoEnum.KYC,
];

// KYC and fiat wallet are handled by JonumKycPromptDialog after lineup
// submission, not by the personal-info AccountVerification flow.
const KYC_HANDLED_INFO = new Set<string>([
  JonumMissingUserInfoEnum.KYC,
  JonumMissingUserInfoEnum.FIAT_WALLET,
]);

// Build a Date at UTC midnight so toISOString() preserves the calendar day
// the user picked, regardless of their local timezone.
export const dateOfBirthToUtc = ({
  year,
  month,
  day,
}: {
  year: string;
  month: string;
  day: string;
}) => new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

// Standalone hook for the createJonumVerification mutation so callers that
// only need the silent create (e.g. JonumVerificationDialog's auto-handle for
// non-French users) don't have to register all four guard mutations.
export const useCreateJonumVerification = () => {
  const [createMutation, { loading }] = useMutation(CREATE_JONUM_VERIFICATION, {
    showErrorsWithSnackNotification: true,
  });
  const createVerification = useCallback(
    async () => createMutation({ variables: { input: {} } }),
    [createMutation]
  );
  return { createVerification, loading };
};

export const useJonumVerificationGuard = () => {
  const {
    flags: { jonumUserInfoCollection = false },
  } = useFeatureFlags();

  const { currentUser, fiatWalletAccountable } = useCurrentUserContext();
  const dataLoaded = !!currentUser;
  const jonumVerification = currentUser?.jonumVerification ?? null;

  const { createVerification, loading: createLoading } =
    useCreateJonumVerification();
  const [collectInfoMutation, { loading: collectLoading }] = useMutation(
    COLLECT_JONUM_PERSONAL_INFO,
    { showErrorsWithSnackNotification: true }
  );
  const [playtimeMutation, { loading: playtimeLoading }] = useMutation(
    SET_JONUM_PLAYTIME_LIMIT,
    { showErrorsWithSnackNotification: true }
  );
  const [spendingMutation, { loading: spendingLoading }] = useMutation(
    SET_JONUM_SPENDING_LIMIT,
    { showErrorsWithSnackNotification: true }
  );

  const {
    currentLocation: { countryCode },
  } = useConfigContext();

  const isFrance = countryCode?.toUpperCase() === 'FR';
  const isExempt = jonumVerification?.status === JonumVerificationStatus.EXEMPT;
  const status = jonumVerification?.status;
  const needsCreation = jonumVerification === null;

  const missingInfo = needsCreation
    ? ALL_MISSING_INFO
    : (jonumVerification.missingInfo ?? ([] as JonumMissingUserInfo[]));

  // The gate is only active for French users on the JONUM rollout who haven't
  // been exempted. Everything this hook surfaces (dialogs, KYC prompts, etc.)
  // must be gated on this — otherwise `jonumVerification === null` would
  // spuriously return `kycRequired = true` for every non-French user.
  const gateActive =
    jonumUserInfoCollection && dataLoaded && isFrance && !isExempt;

  const hasInfoToCollect = missingInfo.some(
    info => !KYC_HANDLED_INFO.has(info)
  );

  // Show when the gate is active and either:
  // - verification doesn't exist yet (needs creation)
  // - verification exists with active status and at least one personal-info
  //   field still to collect (KYC alone is handled separately)
  const shouldShow =
    gateActive &&
    (needsCreation ||
      (!!status && ACTIVE_STATUSES.has(status) && hasInfoToCollect));

  const kycRequired =
    gateActive && missingInfo.includes(JonumMissingUserInfoEnum.KYC);

  const kycUnderReview =
    kycRequired &&
    !!fiatWalletAccountable?.kycStatus &&
    [
      FiatWalletKycState.CREATED,
      FiatWalletKycState.VALIDATION_ASKED,
    ].includesValue(fiatWalletAccountable.kycStatus);

  const isProvisionallyVerified =
    gateActive && status === JonumVerificationStatus.PROVISIONALLY_VERIFIED;

  const gracePeriodExpiresAt = jonumVerification?.gracePeriodExpiresAt ?? null;

  const onStepComplete = useCallback(
    async (
      step: AccountVerificationStep,
      data: Partial<AccountVerificationData>
    ) => {
      switch (step) {
        case AccountVerificationStep.INTRO: {
          await createVerification();
          break;
        }
        case AccountVerificationStep.DETAILS_FORM: {
          const { firstName, lastName, dateOfBirth } = data;
          const dob = dateOfBirth ? dateOfBirthToUtc(dateOfBirth) : undefined;
          await collectInfoMutation({
            variables: { input: { firstName, lastName, dateOfBirth: dob } },
          });
          break;
        }
        case AccountVerificationStep.WEEKLY_PLAY_TIME: {
          if (data.weeklyPlayTimeLimit) {
            const minutes = Number(data.weeklyPlayTimeLimit) * 60;
            await playtimeMutation({
              variables: { input: { playtimeLimitWeekly: minutes } },
            });
          }
          break;
        }
        case AccountVerificationStep.WEEKLY_SPEND: {
          if (data.weeklySpendLimit) {
            await spendingMutation({
              variables: {
                input: {
                  spendingLimitWeekly: {
                    amount: String(Number(data.weeklySpendLimit) * 100),
                    currency: 'EUR' as SupportedCurrency,
                  },
                },
              },
            });
          }
          break;
        }
        default:
          break;
      }
    },
    [
      createVerification,
      collectInfoMutation,
      playtimeMutation,
      spendingMutation,
    ]
  );

  const loading =
    createLoading || collectLoading || playtimeLoading || spendingLoading;

  return {
    shouldShow,
    needsCreation,
    kycRequired,
    kycUnderReview,
    isProvisionallyVerified,
    gracePeriodExpiresAt,
    missingInfo,
    onStepComplete,
    loading,
    createVerification,
  };
};
